/**
 * German Translations (Deutsch)
 */

import type { Translations } from '../types';

export const de: Translations = {
  language: 'de',
  languageName: 'Deutsch',

  common: {
    demo: 'DEMO',
    privacy: 'Datenschutz',
    terms: 'Bedingungen',
  },

  privacy: {
    title: 'Datenschutzrichtlinie',
    whatWeStore: 'Was wir speichern',
    whatWeStoreDesc:
      'Wir verarbeiten vorübergehend die Gesichtsgeometrie zur Altersschätzung. Nach der Verifizierung werden keine Bilder oder biometrische Vorlagen gespeichert.',
    whatWeDont: 'Was wir nicht speichern',
    whatWeDontDesc:
      'Wir speichern keine Fotos, Videos oder biometrische Identifikatoren. Ihre Gesichtsdaten werden im Browser verarbeitet und sofort verworfen.',
    whyWeUseIt: 'Warum wir es verwenden',
    whyWeUseItDesc:
      'Die Altersschätzung hilft Websites, Altersbeschränkungen einzuhalten, ohne persönliche Dokumente zu verlangen.',
    readFull: 'Lesen Sie unsere vollständige',
    privacyPolicyLink: 'Datenschutzrichtlinie',
  },

  theme: {
    system: 'System',
    light: 'Hell',
    dark: 'Dunkel',
  },

  face: {
    noFace: 'Kein Gesicht erkannt',
    tooFar: 'Gesicht zu weit',
    tooClose: 'Gesicht zu nah',
    offCenter: 'Gesicht nicht zentriert',
    poorLighting: 'Schlechtes Licht',
    good: 'Analysiere, bitte warte...',
    holdStill: 'Analysiere, bitte warte...',
    holdStillCountdown: 'Stillhalten... {count}',
    processing: 'Verarbeitung...',
    looking: 'Kein Gesicht erkannt',
    centerFace: 'Gesicht nicht zentriert',
    moveCloser: 'Gesicht zu weit',
    moveBack: 'Gesicht zu nah',
    keepInPosition: 'Position halten',
    perfect: 'Perfekt',
    centerInFrame: 'Gesicht ins Bild rücken',
    outsideOval: 'Gesicht außerhalb des Ovals',
  },

  liveness: {
    blink: {
      title: 'Blinzeln Sie',
      hint: 'Blinzeln Sie einmal langsam',
    },
    smile: {
      title: 'Lächeln',
      hint: 'Zeigen Sie ein natürliches Lächeln',
    },
    lookLeft: {
      title: 'Nach links drehen',
      hint: 'Drehen Sie Ihren Kopf langsam nach links',
    },
    lookRight: {
      title: 'Nach rechts drehen',
      hint: 'Drehen Sie Ihren Kopf langsam nach rechts',
    },
    openMouth: {
      title: 'Mund öffnen',
      hint: 'Öffnen Sie Ihren Mund weit',
    },
    lookUp: {
      title: 'Nach oben schauen',
      hint: 'Neigen Sie Ihren Kopf nach oben',
    },
    lookDown: {
      title: 'Nach unten schauen',
      hint: 'Neigen Sie Ihren Kopf nach unten',
    },
    raiseBrows: {
      title: 'Augenbrauen heben',
      hint: 'Heben Sie Ihre Augenbrauen',
    },
    improveLighting: {
      title: 'Beleuchtung verbessern',
      hint: 'Bewegen Sie sich in einen helleren Bereich, um fortzufahren',
    },
  },

  document: {
    selectType: 'Wählen Sie Ihren Dokumententyp',
    captureFront: 'Vorderseite des Dokuments erfassen',
    captureBack: 'Rückseite des Dokuments erfassen',
    processing: 'Ihr Dokument wird verarbeitet...',
    tips: {
      goodLighting: 'Sorgen Sie für gute Beleuchtung',
      flatSurface: 'Legen Sie das Dokument auf eine flache Oberfläche',
      allCorners: 'Stellen Sie sicher, dass alle Ecken sichtbar sind',
      noGlare: 'Vermeiden Sie Blendung und Schatten',
    },
    types: {
      passport: 'Reisepass',
      idCard: 'Personalausweis',
      driversLicense: 'Führerschein',
    },
  },

  ui: {
    continue: 'Weiter',
    next: 'Nächste',
    retry: 'Erneut versuchen',
    startOver: 'Neu beginnen',
    cancel: 'Abbrechen',
    success: 'Altersüberprüfung Bestanden',
    failure: 'Altersüberprüfung Fehlgeschlagen',
    loading: 'Wird geladen...',
    error: 'Ein Fehler ist aufgetreten',
    permissionRequired: 'Berechtigung erforderlich',
    cameraPermission: 'Kamerazugriff ist erforderlich',
    goBack: 'Zurück',
    switchCamera: 'Kamera wechseln',
    completeVerification: 'Abschließen',
    continueToId: 'Weiter zur ID-Prüfung',
  },

  screens: {
    welcome: {
      title: '18+ Altersüberprüfung',
      subtitle: 'Anonyme Altersprüfung',
      startButton: 'Starten',
      secureNote: 'Sicher & Verschlüsselt',
      ageTechBy: 'Alterstechnologie von AppBouncer',
      waitingCamera: 'Warten auf Kamerazugriff',
      allowCamera: 'Kamerazugriff erlauben',
      noBiometric: 'Keine biometrischen Daten gespeichert',
      loading: 'Laden...',
      loadingRequirements: 'Anforderungen laden...',
    },
    permissions: {
      title: 'Laden ...',
      description: 'Bitte erlauben Sie den Kamerazugriff, wenn Sie dazu aufgefordert werden',
      allowButton: 'Kamerazugriff erlauben',
      privacyNote: 'Ihre Daten sind verschlüsselt und sicher',
      goodLighting: 'Gute Beleuchtung!',
      goodLightingHint: 'Stellen Sie sicher, dass Ihr Gesicht gut beleuchtet ist',
      getReady: 'Mach dich bereit',
      removeCoverings: 'Gesichtsbedeckungen entfernen',
      removeGlasses: 'Brille abnehmen',
    },
    facePosition: {
      title: 'Positionieren Sie Ihr Gesicht',
      hint: 'Gesicht zentrieren',
      step: 'Schritt',
      stepOf: 'Schritt {current} von {total}',
      lookingForFace: 'Suche nach Gesicht...',
    },
    livenessCheck: {
      title: 'Lebendigkeitsprüfung',
      followInstructions: 'Folgen Sie den Anweisungen',
      preparing: 'Vorbereitung...',
      complete: 'Lebenderkennung Bestätigt!',
      actionAutoDetected: 'Aktion wird automatisch erkannt',
      failed: 'Lebendigkeitsprüfung fehlgeschlagen',
      failedDescription:
        'Wir konnten Ihre Lebendigkeit nicht verifizieren. Bitte sorgen Sie für gute Beleuchtung, schauen Sie direkt in die Kamera und folgen Sie den Anweisungen auf dem Bildschirm.',
      retryButton: 'Lebenderkennung Wiederholen',
      restartButton: 'Neu Starten',
    },
    ageEstimation: {
      title: 'Stillhalten',
      analyzing: 'Analysiere...',
      analyzingProgress: 'Analysiere Gesicht {current} von {total}...',
      estimatedAge: 'Geschätztes Alter',
      holdStill: 'Stillhalten',
      verified: 'Alter verifiziert',
      verificationUncertain: 'Alter Unsicher',
      estimationComplete: 'Altersüberprüfung abgeschlossen',
      confirmedAge: 'Bestätigt {minAge}+ Jahre alt',
      couldNotConfirm: 'Alter {minAge}+ konnte nicht bestätigt werden',
      ageConfirmed: 'Alter bestätigt',
      aboveMinimumAge: 'Sie erfüllen die Altersanforderung',
      belowMinimumAge: 'Unter dem Mindestalter',
      documentRequired: 'Dokument erforderlich',
      uploadDocumentMessage:
        'Bitte laden Sie einen gültigen Ausweis hoch, um Ihr Alter zu verifizieren',
      idHint: 'Personalausweis, Reisepass oder Führerschein',
      confidenceLevels: 'Konfidenz nach Altersgrenze',
      confidence: 'Konfidenz',
      years: 'Jahre',
      continueToId: 'Weiter zur ID-Prüfung',
      demoComplete: 'Demo-Modus abgeschlossen. In der Produktion würden Sie weitergeleitet.',
      improveLighting: 'Beleuchtung verbessern',
      improveLightingHint: 'Bewegen Sie sich in einen helleren Bereich für genaue Ergebnisse',
    },
    documentSelect: {
      title: 'Dokumenttyp auswählen',
      description: 'Wählen Sie den Typ des Ausweisdokuments, den Sie scannen möchten',
      idCard: 'Personalausweis',
      idCardHint: 'Behördlich ausgestellter Ausweis',
      passport: 'Reisepass',
      passportHint: 'Nur Fotoseite',
      driversLicense: 'Führerschein',
      driversLicenseHint: 'Gültiger Führerschein',
    },
    captureMode: {
      captureFrontSide: 'Vorderseite erfassen',
      captureBackSide: 'Rückseite erfassen',
      chooseMethod: 'Wählen Sie, wie Sie Ihr Dokument erfassen möchten',
      useCamera: 'Kamera verwenden',
      useCameraHint: 'Foto von Ihrem Dokument aufnehmen',
      uploadFile: 'Datei hochladen',
      uploadFileHint: 'Von Ihrem Gerät auswählen',
    },
    documentCamera: {
      frontSide: 'Vorderseite',
      backSide: 'Rückseite',
      positionInFrame: 'Dokument im Rahmen positionieren',
      scanFront: 'Vorderseite des Dokuments scannen',
      scanBack: 'Rückseite des Dokuments scannen',
      keepFlat: 'Dokument flach und gut beleuchtet halten',
      capturePhoto: 'Foto aufnehmen',
    },
    documentPreview: {
      frontPreview: 'Vorderseite Vorschau',
      backPreview: 'Rückseite Vorschau',
      lookingGood: 'Sieht gut aus!',
      checkDetails: 'Stellen Sie sicher, dass alle Details deutlich sichtbar sind',
      imageQuality: 'Bildqualität',
      qualityGood: 'Gut',
      usePhoto: 'Dieses Foto verwenden',
      retakePhoto: 'Foto neu aufnehmen',
    },
    documentCapture: {
      captureFront: 'Vorderseite erfassen',
      captureBack: 'Rückseite erfassen',
      alignDocument: 'Richten Sie das Dokument im Rahmen aus',
      captureButton: 'Aufnehmen',
      retakeButton: 'Neu aufnehmen',
      confirmButton: 'Bestätigen',
      frontSide: 'Vorderseite',
      backSide: 'Rückseite',
      positionDocument: 'Positionieren Sie Ihr Dokument im Rahmen',
      alignWithFrame: 'Dokument am Rahmen ausrichten',
      positionFrontId: 'Vorderseite des Ausweises positionieren',
      positionBackId: 'Rückseite des Ausweises positionieren',
      scanFrontId: 'Vorderseite des Ausweises scannen',
      scanBackId: 'Rückseite des Ausweises scannen',
      positionWithinFrame: 'Positionieren Sie die Vorderseite im Rahmen',
      flipAndScan: 'Drehen Sie Ihren Ausweis um und scannen Sie die Rückseite',
      goodLight: 'Gutes Licht',
      flatSurface: 'Flache Oberfläche',
      frontCaptured: 'Vorderseite erfasst',
      captureIdCard: 'Personalausweis erfassen',
      capturePassport: 'Reisepass erfassen',
      captureDriversLicense: 'Führerschein erfassen',
    },
    processing: {
      title: 'Verarbeitung',
      description: 'Bitte warten Sie während der Verarbeitung',
      analyzingDocument: 'Dokument wird analysiert...',
      almostDone: 'Fast fertig...',
      faceVerified: 'Gesicht verifiziert',
      livenessConfirmed: 'Lebendigkeit bestätigt',
      processingDocuments: 'Dokumente werden verarbeitet...',
    },
    success: {
      title: 'Altersüberprüfung Bestanden',
      description: 'Alters- und Lebendigkeitsprüfung abgeschlossen',
      redirecting: 'Weiterleitung...',
      summary: 'Zusammenfassung',
      faceMatch: 'Gesichtsabgleich',
      liveness: 'Lebendigkeit',
      age: 'Alter',
      idDocument: 'Ausweisdokument',
      pass: 'Bestanden',
      fail: 'Nicht bestanden',
      valid: 'Gültig',
      notApplicable: 'N/V',
      done: 'Fertig',
      feedbackTitle: 'Bitte bewerten Sie Ihre Erfahrung',
      feedbackPlaceholder: 'Sagen Sie uns, wie wir uns verbessern können...',
      skip: 'Überspringen',
      submit: 'Absenden',
    },
    failure: {
      title: 'Altersüberprüfung Fehlgeschlagen',
      description: 'Die Prüfung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.',
      tryAgainButton: 'Erneut versuchen',
      leaveButton: 'Verlassen',
      attemptsLeft: '{{count}} Versuch(e) übrig',
      noAttemptsLeft: 'Keine weiteren Versuche verfügbar',
    },
    error: {
      title: 'Konfiguration erforderlich',
      description:
        'Einige erforderliche Parameter fehlen in der URL. Bitte kontaktieren Sie den Website-Administrator.',
      missingParams: 'Fehlende Parameter',
      requiredParams: 'Erforderliche Parameter',
      technicalDetails: 'Technische Details',
      urlMustInclude: 'URL muss diese Abfrageparameter enthalten:',
    },
    underage: {
      title: 'Altersanforderung nicht erfüllt',
      description:
        'Sie müssen mindestens {{minAge}} Jahre alt sein, um auf diesen Dienst zuzugreifen.',
    },
    consent: {
      title: 'Helfen Sie, die Altersprüfung zu Verbessern',
      description:
        'Erlauben Sie AppBouncer, eine kleine Menge anonymisierter Nutzungsdaten (z.B. Feed-Aufnahmen) zu sammeln, um die Altersverifizierung und Fallback-Prüfungen zu verbessern. Ihre Daten werden niemals verkauft oder an Dritte weitergegeben.',
      allowButton: 'Erlauben',
    },
    documentConsent: {
      title: 'Dokument Erforderlich',
      description:
        'Um die Prüfung abzuschließen, müssen wir Ihr Alter anhand eines offiziellen Dokuments überprüfen. Stimmen Sie dem Hochladen Ihres Ausweises zu?',
      allowButton: 'Mit Dokument Fortfahren',
      declineButton: 'Ablehnen',
      privacyNote: 'Ihr Dokument wird sicher verarbeitet und nach der Prüfung gelöscht.',
    },
    selfDeclaration: {
      title: 'Ihr Geburtsdatum',
      description: 'Bitte geben Sie Ihr Geburtsdatum ein, um fortzufahren',
      day: 'Tag',
      month: 'Monat',
      year: 'Jahr',
      yourAge: 'Ihr Alter:',
      years: 'Jahre',
      confirmButton: 'Weiter',
      months: {
        january: 'Januar',
        february: 'Februar',
        march: 'März',
        april: 'April',
        may: 'Mai',
        june: 'Juni',
        july: 'Juli',
        august: 'August',
        september: 'September',
        october: 'Oktober',
        november: 'November',
        december: 'Dezember',
      },
    },
  },

  lighting: {
    warning: 'Beleuchtungsproblem',
    adjust: 'Passen Sie die Beleuchtung an',
    lowLight: 'Schwaches Licht erkannt',
    tooBright: 'Zu hell',
    poorContrast: 'Schwacher Kontrast',
    improveLighting: 'Bessere Beleuchtung finden',
    moveToLighter: 'In einen helleren Bereich wechseln',
    videoNotReady: 'Video nicht bereit',
  },

  errors: {
    cameraNotFound: 'Keine Kamera auf diesem Gerät gefunden',
    cameraPermissionDenied: 'Kameraberechtigung wurde verweigert',
    cameraInUse: 'Kamera wird von einer anderen Anwendung verwendet',
    cameraNotSupported: 'Kamera unterstützt die erforderlichen Einstellungen nicht',
    cameraSecurityError: 'Kamera erfordert HTTPS-Verbindung',
    networkError: 'Netzwerkfehler. Bitte überprüfen Sie Ihre Verbindung.',
    timeout: 'Zeitüberschreitung. Bitte versuchen Sie es erneut.',
    invalidApiKey: 'Ungültiger API-Schlüssel',
    sessionExpired: 'Sitzung abgelaufen. Bitte beginnen Sie erneut.',
    verificationFailed: 'Prüfung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    unknownError: 'Ein unerwarteter Fehler ist aufgetreten',
    cameraAccessDeniedDetailed: 'Bitte erlauben Sie den Kamerazugriff, um fortzufahren.',
    cameraNotFoundDetailed:
      'Keine Kamera erkannt. Bitte schließen Sie eine Kamera an und versuchen Sie es erneut.',
    cameraInUseDetailed:
      'Die Kamera wird von einer anderen Anwendung verwendet. Bitte schließen Sie diese und versuchen Sie es erneut.',
    cameraOverconstrained:
      'Die Kamera unterstützt die angeforderte Auflösung nicht. Versuche mit Standardeinstellungen...',
    networkTimeout:
      'Die Anfrage hat zu lange gedauert. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    networkOffline: 'Sie scheinen offline zu sein. Bitte überprüfen Sie Ihre Internetverbindung.',
    apiGenericError: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
    faceNotDetected: 'Bitte positionieren Sie Ihr Gesicht im Rahmen.',
    livenessFailed: 'Lebendigkeitsprüfung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    documentCaptureFailed:
      'Dokument konnte nicht erfasst werden. Bitte sorgen Sie für gute Beleuchtung und versuchen Sie es erneut.',
    initializationFailed:
      'Laden fehlgeschlagen. Bitte aktualisieren Sie die Seite und versuchen Sie es erneut.',
    modelLoadFailed:
      'Erforderliche Komponenten konnten nicht geladen werden. Bitte aktualisieren Sie die Seite und versuchen Sie es erneut.',
    unexpectedError: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    configError: 'Konfigurationsfehler. Bitte kontaktieren Sie den Support.',
  },

  steps: {
    faceDetection: 'Gesichtserkennung',
    liveness: 'Lebenderkennung',
    age: 'Altersprüfung',
    documentScan: 'Dokumentenscan',
    selfDeclaration: 'Selbsterklärung',
  },

  status: {
    cameraStarted: 'Kamera gestartet',
    cameraReady: 'Kamera bereit',
    cameraAccessDenied: 'Kamerazugriff verweigert',
    cameraError: 'Kamerafehler',
    cameraNotAvailable: 'Kamera nicht verfügbar',
    noFaceDetected: 'Kein Gesicht erkannt',
    livenessVerified: 'Lebendigkeit bestätigt',
    livenessCheckFailed: 'Lebendigkeitsprüfung fehlgeschlagen',
    verifyingAge: 'Alter wird überprüft...',
    processingDocument: 'Dokument wird verarbeitet...',
    documentProcessed: 'Dokument erfolgreich verarbeitet',
    verificationCancelled: 'Prüfung abgebrochen',
    improveLightingForAge: 'Verbessern Sie die Beleuchtung für genaue Ergebnisse',
    getReady: 'Machen Sie sich bereit',
    preparingLiveness: 'Lebendigkeitsprüfung wird vorbereitet...',
    uploadingSelfie: 'Selfie-Bilder werden hochgeladen...',
    preparingUpload: 'Upload wird vorbereitet...',
    uploadingFront: 'Vorderseite wird hochgeladen...',
    uploadingBack: 'Rückseite wird hochgeladen...',
    processingDocuments: 'Dokumente werden verarbeitet...',
    documentSubmissionFailed:
      'Dokumentenübermittlung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    selectJpegOrPng: 'Bitte wählen Sie ein JPEG- oder PNG-Bild',
    fileTooLarge: 'Datei zu groß. Maximal 5 MB erlaubt.',
    errorReadingFile: 'Fehler beim Lesen der Datei',
    frontDocumentRequired: 'Bild der Vorderseite erforderlich',
    verificationFailedClose: 'Prüfung fehlgeschlagen. Sie werden zurückgeleitet.',
    verificationCompleteClose: 'Prüfung abgeschlossen! Sie werden zurückgeleitet.',
    analyzingFace: 'Gesicht wird analysiert...',
  },
};
